import { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { moment, SYSTEM_TIMEZONE } from '../../utils/dateUtils';

import appointmentsServices from '../../services/appointmentsServices';

const STATUS_COLOR = {
  Cancelled: 'error',
  Active: 'primary',
};

const formatWhen = (value) => {
  if (!value) return 'Not scheduled';
  return moment(value).tz(SYSTEM_TIMEZONE).format('MMM D, YYYY h:mm A');
};

// Search appointments by property address across ALL dates. Results are
// clickable and open the appointment in a new browser tab.
const AddressSearchModal = ({ open, onClose, initialQuery }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const doSearch = useCallback(async (q) => {
    const trimmed = (q || '').trim();
    if (trimmed.length < 3) {
      setError('Enter at least 3 characters');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await appointmentsServices.searchByAddress(trimmed);
      setResults(response.data || []);
    } catch (err) {
      console.error('Address search failed:', err);
      setError(err?.response?.data?.message || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-arm the modal each time it opens; auto-search when a query was
  // already typed on the page.
  useEffect(() => {
    if (!open) return;
    const q = initialQuery || '';
    setQuery(q);
    setResults(null);
    setError(null);
    if (q.trim().length >= 3) {
      doSearch(q);
    }
  }, [open, initialQuery, doSearch]);

  const openAppointment = (id) => {
    window.open(`/appointments/${id}`, '_blank', 'noopener');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Appointment history by address</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Street, city or zip — e.g. 2416 Turning Trail"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') doSearch(query);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={() => doSearch(query)} disabled={loading}>
            Search
          </Button>
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && results && results.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No appointments found for this address
          </Typography>
        )}

        {!loading && results && results.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary">
              {results.length} appointment{results.length === 1 ? '' : 's'} found — click to open in a new tab
            </Typography>
            <List dense sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {results.map((item) => (
                <ListItemButton
                  key={item.id}
                  onClick={() => openAppointment(item.id)}
                  sx={{ borderBottom: '1px solid #eee', alignItems: 'flex-start', gap: 1 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatWhen(item.startTime)}
                      </Typography>
                      {item.type?.name && (
                        <Chip size="small" variant="outlined" label={item.type.name} />
                      )}
                      {item.status && (
                        <Chip
                          size="small"
                          label={item.status}
                          color={STATUS_COLOR[item.status] || 'default'}
                          variant={item.status === 'Cancelled' ? 'filled' : 'outlined'}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                      {item.user?.fullName || 'Unassigned'}
                      {item.client?.fullName ? ` — ${item.client.fullName}` : ''}
                    </Typography>
                    {item.address && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {item.address}
                      </Typography>
                    )}
                  </Box>
                  <OpenInNewIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.5 }} />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressSearchModal;
